import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { EditablePageShell } from "@/components/site/EditablePageShell";
import { pageQueryOptions } from "@/lib/pages.functions";
import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/tracking";
import { SHARE_IMAGES } from "@/lib/share-images";

export const Route = createFileRoute("/amazon")({
  loader: ({ context }) => context.queryClient.ensureQueryData(pageQueryOptions("amazon")),
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData?.title || "Buy on Amazon — Glow Long Golf" },
      { name: "description", content: loaderData?.meta_description || "Prefer Amazon? Glow Long is available on our site and — once listed — on Amazon. Pick whichever works for you." },
      { property: "og:title", content: loaderData?.title || "Buy on Amazon — Glow Long Golf" },
      { property: "og:description", content: loaderData?.meta_description || "Prefer Amazon? Glow Long is available on our site and on Amazon. Pick whichever works for you." },
      { property: "og:url", content: "https://glowlonggolf.com/amazon" },
      { property: "og:image", content: SHARE_IMAGES.fairwayArc },
      { name: "twitter:image", content: SHARE_IMAGES.fairwayArc },
    ],
    links: [{ rel: "canonical", href: "https://glowlonggolf.com/amazon" }],
  }),

  errorComponent: ({ error }) => <div className="p-8 text-sm text-destructive" role="alert">{error.message}</div>,
  notFoundComponent: () => <div className="p-8 text-sm text-muted-foreground">Not found.</div>,
  component: AmazonPage,
});

function AmazonPage() {
  const { data: cfg } = useQuery({
    queryKey: ["amazon-storefront"],
    queryFn: async () => {
      const { data } = await supabase.rpc("get_amazon_storefront");
      return data?.[0] ?? null;
    },
  });

  return (
    <EditablePageShell slug="amazon" defaults={{ eyebrow: "Available on Amazon", title: "Buy direct or buy on Amazon", lead: "Both routes get you the same Glow Long golf balls. Pick whichever feels easier." }}>
      <section className="mx-auto grid max-w-5xl gap-6 px-4 py-12 sm:px-6 md:grid-cols-2 lg:px-8">
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold">Shop direct</h2>
          <p className="mt-2 text-muted-foreground">Fastest path. Free shipping over $50. Order ships from Amazon's fulfillment network either way.</p>
          <Link to="/shop" className="mt-4 inline-flex rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-glow hover:opacity-90">Shop now</Link>
        </div>
        <div className="rounded-3xl border border-border bg-card p-6">
          <h2 className="font-display text-xl font-semibold">Shop on Amazon</h2>
          {cfg?.amazon_store_url ? (
            <>
              <p className="mt-2 text-muted-foreground">Already a Prime member? Order from our Amazon storefront with one-click checkout.</p>
              <a
                href={cfg.amazon_store_url}
                target="_blank"
                rel="noreferrer"
                onClick={() => track("click_amazon_cta", { location: "amazon_page" })}
                className="mt-4 inline-flex rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium hover:bg-secondary"
              >
                Open Amazon store
              </a>
            </>
          ) : (
            <>
              <p className="mt-2 text-muted-foreground">We're not on Amazon just yet — listing is in progress. Drop your email and we'll let you know the moment we're live.</p>
              <Link to="/" className="mt-4 inline-flex rounded-full border border-border bg-background px-5 py-2.5 text-sm font-medium hover:bg-secondary">
                Notify me
              </Link>
            </>
          )}
        </div>
      </section>
    </EditablePageShell>
  );
}
